import { useCallback, useEffect, useRef, useState } from 'react'
import { browseFolderAll, browseFolderDeep, filterTree, CANVA_SOURCE, sharedCanvaStatus, connectionGap } from './canvaSource.js'
import { STUDIO_FOLDERS, FOLDER_NAMES, CANVA_ROOT } from './folders.js'
import { refLabel } from './buildPayload.js'
import { ingestUrlAsOwned, uploadOwnedFile } from '../ownedAsset.js'
import { attachFailure, refLine } from './assetFailureCopy.js'
import styles from './Studio.module.css'

/**
 * THE FOLDER-SCOPED PICKER (Master Spec Section 2 / Section 11).
 *
 * Opens on ONE folder, drills into its subfolders with a breadcrumb, shows
 * thumbnails, and searches **only inside the folded-in folder's subtree** —
 * never all of Canva. The scoping is enforced server-side
 * (`canva.js#searchInFolder` walks the subtree of the `folderId` it is given),
 * so this component cannot widen it by accident.
 *
 * TWO WAYS TO SUPPLY THE COMPONENT, AND BOTH ARE ALWAYS ON SCREEN.
 * A picker whose only visible control is "Browse" is useless the moment the
 * Canva feed is down — which is exactly how this step came to read as nothing
 * but "None". So the manual half (an asset reference + an upload straight to
 * HubSpot File Manager) sits in the step body permanently, beside the browse
 * button, rather than behind the disclosure. The operator can always supply a
 * component; the feed only decides whether they get to pick one visually.
 *
 * Three sources, and the UI says which one it is on:
 *   live    — the connected Canva account
 *   manual  — Canva unreachable: the reference field and the upload are the path
 *   demo    — ?demo=1 only, and badged
 *
 * AND IT SHOWS THE WHOLE SUBTREE BY DEFAULT. Bezel & Top Designs keeps a dozen
 * designs at its top level and the other 300+ in sport sub-folders, so a picker
 * that listed only the opened folder was showing the operator a fraction of the
 * library and calling it the folder. `browseFolderDeep` walks every folder
 * underneath, each design carries the sub-folder it came from, and the
 * sub-folder tiles are still there to drill into. "This folder only" is one
 * click away for anyone who wants the old, narrower view.
 */

/** Tiles rendered before "Show more" — 300+ <img> at once is a mobile stall. */
const TILE_PAGE = 24

export default function CanvaFolderPicker({ folderKey = 'root', value, onChange, label = 'Design' }) {
  const home = STUDIO_FOLDERS[folderKey] || STUDIO_FOLDERS.root
  const [trail, setTrail] = useState([{ id: home.id, name: home.name }])
  // Flattened-subtree view. ON for an element folder (Bezel & Top, Side Shanks,
  // …) because that is where the designs actually live; OFF at the engine ROOT,
  // where the folder structure IS the thing being navigated and a flattened
  // walk of the entire account would be noise.
  const [deep, setDeep] = useState(home.id !== CANVA_ROOT.id)
  const [visible, setVisible] = useState(TILE_PAGE)
  const [node, setNode] = useState(null)
  const [loading, setLoading] = useState(false)
  const [paging, setPaging] = useState(false)
  const [q, setQ] = useState('')
  const [open, setOpen] = useState(false)
  const [manual, setManual] = useState('')
  const [uploading, setUploading] = useState(false)
  const [uploadErr, setUploadErr] = useState(null)
  const [picking, setPicking] = useState('')
  const [pickErr, setPickErr] = useState(null)
  /* The retry ladder is seven seconds long. A button that just sits there for
   * seven seconds reads as a hang, and the rep clicks it again — which is a
   * second upload of the same bytes. This says what is happening instead. */
  const [retrying, setRetrying] = useState('')
  const [feed, setFeed] = useState(null)
  const abortRef = useRef(null)
  const fileRef = useRef(null)
  const nodeRef = useRef(null)

  const here = trail[trail.length - 1]
  nodeRef.current = node

  // One shared answer for the whole studio — see canvaSource#sharedCanvaStatus.
  useEffect(() => {
    let alive = true
    sharedCanvaStatus().then((s) => { if (alive) setFeed(s) })
    return () => { alive = false }
  }, [])

  /**
   * ONE folder, ALL of it. `browseFolderAll` follows the feed's continuation
   * token until it is empty; `onPage` lands each page on screen as it arrives,
   * so a 300-design folder starts rendering on page 1 instead of after the last
   * one. `paging` is what tells the operator more is still coming — a folder
   * that quietly stopped at page 1 is the bug this replaces.
   */
  const load = useCallback(async (folderId, query, wholeTree) => {
    abortRef.current?.abort()
    const ctrl = new AbortController()
    abortRef.current = ctrl
    setLoading(true)
    setPaging(false)
    setVisible(TILE_PAGE)
    try {
      const progress = (page) => {
        if (ctrl.signal.aborted) return
        setNode(page)
        setPaging(!!page.loading)
        setLoading(false)
      }
      // THE WHOLE SUBTREE. Every folder under this one, walked breadth-first and
      // landing on screen folder by folder — a 300+ design library fills the
      // grid as it arrives instead of after the last request.
      const n = wholeTree
        ? await browseFolderDeep(folderId, { signal: ctrl.signal, onProgress: progress })
        : await browseFolderAll(folderId, { q: query, signal: ctrl.signal, onPage: progress })
      if (!ctrl.signal.aborted) setNode(n)
    } catch (err) {
      if (err?.name !== 'AbortError') {
        setNode({ id: folderId, name: FOLDER_NAMES[folderId] || 'Folder', folders: [], items: [], source: CANVA_SOURCE.MANUAL, reason: String(err?.message || err) })
      }
    } finally {
      if (!ctrl.signal.aborted) { setLoading(false); setPaging(false) }
    }
  }, [])

  /** The cap was hit — keep going from the token the loop handed back. */
  const loadMore = useCallback(async () => {
    const current = nodeRef.current
    if (!current?.continuation) return
    const ctrl = abortRef.current
    setPaging(true)
    try {
      const n = await browseFolderAll(current.id, {
        q: q.trim(),
        signal: ctrl?.signal,
        seed: current,
        continuation: current.continuation,
        onPage: (page) => { if (!ctrl?.signal?.aborted) setNode(page) },
      })
      if (!ctrl?.signal?.aborted) setNode(n)
    } catch (err) {
      if (err?.name !== 'AbortError') setPaging(false)
    } finally {
      setPaging(false)
    }
  }, [q])

  /**
   * DEEP: the walk depends on the folder and the mode, NEVER on the query.
   * Search in this mode is a client-side filter over what is already collected
   * (`filterTree`), which is both instant and — unlike the backend's scoped
   * search, which hard-caps at 240 items and returns no cursor — able to see
   * every design in a 300+ folder.
   */
  useEffect(() => {
    if (!open || !deep) return undefined
    load(here.id, '', true)
    return undefined
  }, [open, deep, here.id, load])

  /** SHALLOW: the original behaviour — one folder, server-side scoped search. */
  useEffect(() => {
    if (!open || deep) return undefined
    const t = setTimeout(() => load(here.id, q.trim(), false), q.trim() ? 260 : 0)
    return () => clearTimeout(t)
  }, [open, deep, here.id, q, load])

  // A new search shows the top of the result set, not page 3 of the last one.
  useEffect(() => { setVisible(TILE_PAGE) }, [q])

  useEffect(() => () => abortRef.current?.abort(), [])

  function enter(folder) {
    setQ('')
    setTrail((t) => [...t, { id: folder.id, name: folder.name }])
  }
  function upTo(i) {
    setQ('')
    setTrail((t) => t.slice(0, i + 1))
  }

  /* ---- PICKING A DESIGN IS AN INGESTION, NOT A LINK -------------------
   *
   * This used to store `{ id, name, source: 'Canva', url }`, where `url` was
   * the design on Canva's own servers. That breaks the owned-assets rule in
   * its sharpest form: a Canva preview URL is SIGNED AND EXPIRING, so a build
   * recorded with one holds a reference that stops resolving by itself, long
   * after anybody could tell which design it was.
   *
   * So the bytes are copied into HubSpot File Manager on the click, exactly as
   * the upload button does, and the OWNED url is what goes on the build.
   *
   * IF THE COPY FAILS, NOTHING IS ATTACHED. Falling back to the Canva link
   * would restore the defect at the precise moment it is most likely to bite,
   * and a half-owned ref is indistinguishable from an owned one once it is on
   * a build. The operator is told what to do instead, and the design is still
   * on screen to try again.
   *
   * WHAT IS OWNED IS THE PREVIEW IMAGE the feed gives us, which is what the
   * picker renders and all the API exposes without a Canva export job. It is
   * labelled as a preview rather than passed off as the production artwork.
   * -------------------------------------------------------------------- */
  async function pick(item) {
    setPickErr(null)
    // The canned tree is data-URL swatches with no owned file behind them, and
    // it is badged "demo library" throughout. Nothing to copy, nothing to claim.
    if (item?.source === 'demo' || source === CANVA_SOURCE.DEMO) {
      onChange({ id: item.id, name: item.name, source: 'demo library — not an owned asset' })
      setOpen(false)
      return
    }

    /* ⚠️ THE POLICY CHECK RUNS FIRST, BEFORE ANYTHING IS ATTEMPTED.
     * If this design has no copyable image then the only thing we could store
     * is the Canva link, and that is refused on policy. Saying so here means
     * the Canva sentence can only ever appear when Canva is genuinely the
     * reason — it can no longer be bolted onto a storage failure. */
    if (!/^https?:\/\//i.test(String(item?.thumb || ''))) {
      setPickErr(attachFailure(item?.name, { kind: 'canva-link' }))
      return
    }

    setPicking(item.id)
    setRetrying('')
    try {
      const owned = await ingestUrlAsOwned(item.thumb, {
        name: item.name,
        kind: folderKey,
        onAttempt: ({ attempt, of }) => setRetrying(`Storage did not answer. Retrying (${attempt + 1} of ${of})…`),
      })
      onChange({
        id: owned.fileId,
        name: item.name,
        source: 'Canva preview — owned copy in HubSpot File Manager',
        url: owned.url,
      })
      setOpen(false)
    } catch (err) {
      /* Nothing is attached on failure, and the design is still on screen to
       * try again. A half-owned ref is indistinguishable from an owned one. */
      setPickErr(attachFailure(item?.name, err))
    } finally {
      setPicking('')
      setRetrying('')
    }
  }

  function applyManual() {
    const v = manual.trim()
    if (!v) return
    onChange({ name: v, source: 'manual reference' })
    setManual('')
  }

  /* ⚠️ THIS FAILS CLOSED NOW, AND THAT IS THE MOST IMPORTANT LINE IN THE FILE.
   *
   * It used to call `onChange({ name: file.name, source: 'manual reference —
   * not yet owned' })` in the catch, on the reasoning that a dead upload route
   * should not cost the operator the step. What that actually did was put a
   * NAMED asset on a build with no file behind it: the record claimed an asset
   * it did not have, the build looked complete, and a rep could send it to
   * production. That is precisely the owned-assets violation the rule exists to
   * prevent, and `pick` two functions up has always refused to do it.
   *
   * Nothing is attached. The file is still on disk and the button is still
   * there. A blank the rep can see beats a reference nobody can resolve. */
  async function upload(file) {
    if (!file) return
    setUploading(true)
    setUploadErr(null)
    setRetrying('')
    try {
      const owned = await uploadOwnedFile(file, {
        kind: folderKey,
        onAttempt: ({ attempt, of }) => setRetrying(`Storage did not answer. Retrying (${attempt + 1} of ${of})…`),
      })
      onChange({ id: owned.fileId, name: file.name, source: 'uploaded — owned in HubSpot File Manager', url: owned.url })
      setOpen(false)
    } catch (err) {
      setUploadErr(attachFailure(file?.name, err))
    } finally {
      setUploading(false)
      setRetrying('')
    }
  }

  const source = node?.source
  const chosen = refLabel(value)
  const gap = connectionGap(feed)
  const browseLabel = open ? 'Close browser' : `Browse ${home.name}`

  // In deep mode the search is applied here, over the whole collected subtree.
  // In shallow mode the backend already applied it, so the list is used as-is.
  const allItems = node?.items || []
  const shown = deep ? filterTree(allItems, q) : allItems
  const tiles = shown.slice(0, visible)

  return (
    <div className={styles.picker}>
      <div className={styles.pickerHead}>
        <div className={styles.pickerValue}>
          {chosen ? (
            <>
              <span className={styles.pickerChosen}>{chosen}</span>
              {value?.source && <span className={styles.pickerSrc}>{value.source}</span>}
            </>
          ) : (
            <span className={styles.pickerEmpty}>Nothing supplied yet — browse the folder, or attach one below.</span>
          )}
        </div>
        {chosen && (
          <button type="button" className={styles.linkBtn} onClick={() => onChange(null)}>Clear</button>
        )}
      </div>

      {/* THE TWO PATHS, both always here. Neither is behind a disclosure. */}
      <div className={styles.pickerActions}>
        <button type="button" className="btn" onClick={() => setOpen((o) => !o)}>
          {browseLabel}
        </button>
        <button type="button" className="btn" onClick={() => fileRef.current?.click()} disabled={uploading}>
          {uploading ? 'Uploading…' : 'Upload from this device'}
        </button>
        <input
          ref={fileRef}
          type="file"
          accept="image/*,.pdf,.svg"
          hidden
          onChange={(e) => { upload(e.target.files?.[0]); e.target.value = '' }}
        />
      </div>

      <div className={styles.attachRow}>
        <input
          className="input"
          aria-label={`${label} — asset reference`}
          placeholder="Or type an asset reference — e.g. “R3652 domed football top”"
          value={manual}
          onChange={(e) => setManual(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); applyManual() } }}
        />
        <button type="button" className="btn" onClick={applyManual} disabled={!manual.trim()}>Attach reference</button>
      </div>
      {/* The retry ladder, said out loud. Seven silent seconds reads as a hang. */}
      {retrying && <p className={styles.note} role="status">{retrying}</p>}
      {/* TWO STATES, rendered from the same shape but never sharing wording:
          a policy refusal carries no reference because nothing was attempted. */}
      {[uploadErr, pickErr].filter(Boolean).map((f, i) => (
        <p key={i} className={styles.warnNote}>
          <strong>{f.headline}</strong> {f.fix}
          {f.ref ? ` ${refLine(f.ref)}` : ''}
        </p>
      ))}
      {gap && !open && (
        <p className={styles.note}>
          <span className={`${styles.badge} ${styles.badgeWarn}`}>manual only</span> {gap.headline} Attach or upload the
          design here — an upload is owned in HubSpot File Manager.
        </p>
      )}

      {open && (
        <div className={styles.pickerBody}>
          <div className={styles.crumbs}>
            {trail.map((c, i) => (
              <span key={c.id + i}>
                {i > 0 && <span className={styles.crumbSep}>/</span>}
                <button
                  type="button"
                  className={styles.crumb}
                  disabled={i === trail.length - 1}
                  onClick={() => upTo(i)}
                >
                  {c.name}
                </button>
              </span>
            ))}
            {!home.verified && (
              <span className={styles.flag} title="Master Spec Section 12, open confirmation #1">
                folder unconfirmed
              </span>
            )}
          </div>

          <div className={styles.pickerBar}>
            <input
              className="input"
              placeholder={deep
                ? `Search ${here.name} and every folder in it`
                : `Search inside ${here.name} only`}
              value={q}
              onChange={(e) => setQ(e.target.value)}
            />
            {/* The narrower view is one click away — but it is not the default,
                because the designs live in the sub-folders. */}
            <button
              type="button"
              className={styles.linkBtn}
              onClick={() => { setQ(''); setDeep((d) => !d) }}
            >
              {deep ? 'This folder only' : 'Include sub-folders'}
            </button>
            {source === CANVA_SOURCE.LIVE && <span className={`${styles.badge} ${styles.badgeOk}`}>live Canva</span>}
            {source === CANVA_SOURCE.DEMO && <span className={`${styles.badge} ${styles.badgeWarn}`}>demo library</span>}
            {source === CANVA_SOURCE.MANUAL && <span className={`${styles.badge} ${styles.badgeWarn}`}>manual only</span>}
          </div>

          {/* The feed is down: say what is missing, not "no designs". */}
          {gap && (
            <p className={styles.warnNote}>
              <strong>{gap.headline}</strong> {gap.fix}
            </p>
          )}
          {node?.reason && !gap && <p className={styles.note}>{node.reason}</p>}
          {loading && <p className={styles.note}>Loading {here.name}…</p>}

          {!loading && node && (
            <>
              {!!node.folders?.length && (
                <div className={styles.folderRow}>
                  {node.folders.map((f) => (
                    <button key={f.id} type="button" className={styles.folder} onClick={() => enter(f)}>
                      <span className={styles.folderIcon} aria-hidden>▸</span>
                      {f.name}
                    </button>
                  ))}
                </div>
              )}

              {shown.length ? (
                <>
                  <div className={styles.grid}>
                    {tiles.map((it) => (
                      <button
                        key={it.id}
                        type="button"
                        className={styles.tile}
                        disabled={!!picking}
                        onClick={() => pick(it)}
                      >
                        <span className={styles.tileThumb}>
                          {it.thumb ? <img src={it.thumb} alt="" loading="lazy" /> : <span className={styles.tileNoThumb}>no preview</span>}
                        </span>
                        <span className={styles.tileName}>
                          {picking === it.id ? 'Copying into File Manager…' : it.name}
                        </span>
                        {/* Which sub-folder this came from. A flat grid of 300
                            tiles is useless if a football top and a wrestling
                            one look the same. */}
                        {deep && it.folderPath && it.folderId !== here.id && (
                          <span className={styles.tileFolder}>{it.folderPath}</span>
                        )}
                      </button>
                    ))}
                  </div>
                  {shown.length > tiles.length && (
                    <button
                      type="button"
                      className="btn"
                      onClick={() => setVisible((v) => v + TILE_PAGE)}
                    >
                      Show {Math.min(TILE_PAGE, shown.length - tiles.length)} more
                      {' '}— {shown.length - tiles.length} still hidden
                    </button>
                  )}
                </>
              ) : (
                !node.folders?.length && !gap && !paging && (
                  <p className={styles.note}>
                    {q.trim()
                      ? `No design in ${here.name} matches “${q.trim()}”.`
                      : `${here.name} has no designs the engine can see yet.`}
                  </p>
                )
              )}

              {/* THE FOLDER'S OWN RECEIPT. A count plus the state of the paging
                  loop, so 300+ designs never read as "that is all there is". */}
              {(!!shown.length || paging) && (
                <p className={styles.note}>
                  {shown.length} design{shown.length === 1 ? '' : 's'}
                  {deep && q.trim() ? ` of ${allItems.length}` : ''}
                  {node.folders?.length ? ` · ${node.folders.length} subfolder${node.folders.length === 1 ? '' : 's'}` : ''}
                  {paging
                    ? deep
                      ? ` — reading ${here.name} and its sub-folders…`
                      : ' — loading the rest of this folder…'
                    : node.continuation
                      ? ' — more to load.'
                      : q.trim()
                        ? ' matching your search.'
                        : deep
                          // The claim the whole fix exists to make good on.
                          ? ` — every design in ${here.name} and its ${node.scanned > 1 ? `${node.scanned - 1} ` : ''}sub-folder${node.scanned === 2 ? '' : 's'}.`
                          : ' — the whole folder.'}
                  {node.truncated && ` ${node.reason}`}
                  {node.partial && !node.truncated && ` ${node.reason || 'The feed stopped part-way; this is what loaded.'}`}
                </p>
              )}
              {node.continuation && !paging && (
                <button type="button" className="btn" onClick={loadMore}>Load more designs</button>
              )}
            </>
          )}
        </div>
      )}
    </div>
  )
}
